"use client";
import { useTranslation } from "@/lib/i18n";

import React from "react";
import { User } from "lucide-react";

type Testimonial = {
    name: string;
    username: string;
    avatar: string;
    content: string;
};

const testimonials = (t: any): Testimonial[] => [
    {
        name: "Abdulrahim Al Raheeli",
        username: t("testimonials.c1"),
        avatar: "/avatars/1.png",
        content: t("testimonials.r1"),
    },
    {
        name: "Ghassen Dhane",
        username: t("testimonials.c2"),
        avatar: "/avatars/2.png",
        content: t("testimonials.r2"),
    },
    {
        name: "Sabri Ben Radhia",
        username: t("testimonials.c3"),
        avatar: "/avatars/3.png",
        content: t("testimonials.r3"),
    },
    {
        name: "Riyadh Bousslema",
        username: t("testimonials.c4"),
        avatar: "/avatars/5.png",
        content: t("testimonials.r4"),
    },
    {
        name: "Leilla Sabri",
        username: t("testimonials.c5"),
        avatar: "/avatars/6.png",
        content: t("testimonials.r5"),
    },
];

const TestimonialCard: React.FC<Testimonial> = ({
    name,
    username,
    avatar,
    content,
}) => (
    <a
        className="flex max-w-[320px] flex-col rounded-lg glass-3 p-4 text-start shadow-md sm:max-w-[420px] sm:p-6 hover:from-card/30 hover:to-card/20"
        href="#"
    >
        <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <User className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex flex-col items-start">
                <h3 className="text-md font-semibold leading-none">{name}</h3>
                <p className="text-sm text-muted-foreground">{username}</p>
            </div>
        </div>
        <p className="sm:text-md mt-4 text-sm text-muted-foreground">
            {content.split("@aicompliance").map((part, index) => (
                <React.Fragment key={index}>
                    {index > 0 && (
                        <span className="text-aicompliance-foreground">
                            @aicompliance
                        </span>
                    )}
                    {part}
                </React.Fragment>
            ))}
        </p>
    </a>
);

const Testimonials: React.FC = () => {
    const { t } = useTranslation("common");
    return (
        <section className="md:py-32 w-full overflow-hidden px-0" id="about">
            <div className="mx-auto flex max-w-container flex-col items-center gap-4 text-center sm:gap-16">
                <div className="flex flex-col items-center gap-4 px-4 sm:gap-8">
                    <h2 className="max-w-[720px] text-3xl font-semibold leading-tight sm:text-5xl sm:leading-tight">
                        {t("testimonials.title")}
                    </h2>
                    <p className="text-md max-w-[600px] font-medium text-muted-foreground sm:text-xl">
                        {t("testimonials.subtitle")}
                    </p>
                </div>
                <div className="relative flex w-full flex-col items-center justify-center overflow-hidden">
                    <div className="group flex overflow-hidden p-2 [--gap:1rem] [gap:var(--gap)] flex-row [--duration:40s]">
                        {[...Array(4)].map((_, groupIndex) => (
                            <div
                                key={groupIndex}
                                className="flex shrink-0 justify-around [gap:var(--gap)] animate-marquee flex-row group-hover:[animation-play-state:paused]"
                            >
                                {testimonials(t).map((testimonial, index) => (
                                    <TestimonialCard
                                        key={`${groupIndex}-${index}`}
                                        {...testimonial}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                    <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-1/3 bg-gradient-to-r from-background sm:block"></div>
                    <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/3 bg-gradient-to-l from-background sm:block"></div>
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
